import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import classNames from 'classnames';
import { Select } from 'alurkerja-ui';
import { AlurkerjaInputType } from '../../types/InputType';
import useFormEditorState from '../../state/formeditor.state';
import { Label } from 'bpmn-js/lib/model/Types';

interface MultipleRemoteSelectProps {
  props: AlurkerjaInputType;
}

export const MultipleRemoteSelect = ({ props }: MultipleRemoteSelectProps) => {
  const {
    item,
    form: { control, getValues },
  } = props;

  const { axiosInstance } = useFormEditorState();
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultValue = getValues(item.name);

  useEffect(() => {
    if (item.data_url) {
      if(axiosInstance) {
        fetchRemoteData();
      }
      fetchRemoteData();
    }
  }, [item.data_url , axiosInstance]);

  const fetchRemoteData = async () => {
    if (!item.data_url) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(item.data_url);
      let data = response.data;

      // Navigate to nested data if root_data is specified
      if (item.root_data) {
        const paths = item.root_data.split('.');
        for (const path of paths) {
          if (data && typeof data === 'object' && path in data) {
            data = data[path];
          } else {
            throw new Error(`Path '${item.root_data}' not found in response`);
          }
        }
      }

      if (Array.isArray(data)) {
        const formattedOptions = data.map((option) => ({
          value: String(getLabelValue(option, item.select_remote_value_field || 'id')),
          label: String(getLabelValue(option, item.select_remote_label_field || 'name')),
        }));
        setOptions(formattedOptions);
      } else {
        throw new Error('Data is not an array');
      }
    } catch (err: any) {
      console.error('Error fetching remote data:', err);
      setError(err.message || 'Failed to fetch data');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const getLabelValue = (option: any, field: string) => {
    if (!field) return '';
    const paths = field.split('.');
    let value = option;
    for (const path of paths) {
      if (value && typeof value === 'object' && path in value) {
        value = value[path];
      } else {
        return '';
      }
    }
    return value;
  };

  const getCurrentLanguage = () => {
    // You can get this from context or state management
    return 'en'; // Default to English
  };

  const getPlaceholder = () => {
    const lang = getCurrentLanguage();
    
    if (typeof item.placeholder === 'object' && item.placeholder !== null) {
      return item.placeholder[lang as keyof typeof item.placeholder] || (item.placeholder as Label).en || 'Select options';
    }
    return item.placeholder || 'Select options';
  };

  const getLabel = () => {
    const lang = getCurrentLanguage();
    if (typeof item.label === 'object' && item.label !== null) {
      return item.label[lang as keyof typeof item.label] || item.label.en || '';
    }
    return item.label || '';
  };

  return (
    <div>
      <label
        className={classNames('font-medium text-sm', {
          'after:content-["*"] after:text-red-400 after:text-sm': item.constraints?.required,
        })}
        htmlFor={item.name}
      >
        {getLabel()}
      </label>
      <Controller
        name={item.name}
        control={control}
        defaultValue={defaultValue || []}
        render={({ field, fieldState }) => {
          // Convert string values to array if needed (for backward compatibility)
          const fieldValue = Array.isArray(field.value) ? field.value : 
                            field.value ? [field.value] : [];
          
          // Find selected options based on field values
          const selectedOptions = options.filter((opt) => 
            fieldValue.includes(opt.value)
          );

          return (
            <div>
              <Select
                value={selectedOptions}
                options={options}
                onChange={(selectedItems: any) => {
                  // Handle both array and single item selection
                  const values = Array.isArray(selectedItems) 
                    ? selectedItems.map((item: any) => item?.value || item).filter(Boolean)
                    : selectedItems?.value ? [selectedItems.value] : [];
                  
                  field.onChange(values);
                }}
                isMulti={true}
                isSearchable={true}
                isDisabled={item.disabled || loading}
                placeholder={loading ? 'Loading...' : error ? 'Error loading data' : getPlaceholder()}
                isLoading={loading}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                // Additional props for better multiple selection UX
                isClearable={true}
                backspaceRemovesValue={true}
              />
              {fieldState.error && (
                <p className="text-sm text-red-500 mt-1">{fieldState.error.message}</p>
              )}
              {error && !fieldState.error && (
                <p className="text-sm text-orange-500 mt-1">{error}</p>
              )}
              {/* Display selected count for better UX */}
              {selectedOptions.length > 0 && !error && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedOptions.length} item{selectedOptions.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          );
        }}
      />
    </div>
  );
};